import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global meta-tag override. Runs on every route change. If public.entity_seo
 * has a row for the current pathname, its meta_title / meta_description /
 * og:* / twitter:* / keywords replace whatever the page component wrote.
 *
 * This lets every public URL ship unique, AI-generated SEO metadata without
 * editing individual page components.
 */
const EntitySeoAutoLoader = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;

    // Skip admin/auth/private routes – those stay noindex and don't need overrides.
    if (
      pathname.startsWith("/admin") ||
      pathname.startsWith("/auth") ||
      pathname.startsWith("/dashboard") ||
      pathname.startsWith("/onboarding")
    ) return;

    (async () => {
      const { data } = await supabase
        .from("entity_seo")
        .select("meta_title,meta_description,keywords_primary,keywords_secondary")
        .eq("route_path", pathname)
        .maybeSingle();

      if (cancelled || !data) return;

      // Title
      if (data.meta_title) document.title = data.meta_title;

      const setMeta = (selector: string, attr: "name" | "property", key: string, value: string) => {
        let el = document.head.querySelector<HTMLMetaElement>(selector);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute("content", value);
      };

      if (data.meta_description) {
        setMeta('meta[name="description"]', "name", "description", data.meta_description);
        setMeta('meta[property="og:description"]', "property", "og:description", data.meta_description);
        setMeta('meta[name="twitter:description"]', "name", "twitter:description", data.meta_description);
      }
      if (data.meta_title) {
        setMeta('meta[property="og:title"]', "property", "og:title", data.meta_title);
        setMeta('meta[name="twitter:title"]', "name", "twitter:title", data.meta_title);
      }
      const kws = [
        ...(data.keywords_primary ?? []),
        ...(data.keywords_secondary ?? []),
      ];
      if (kws.length) {
        setMeta('meta[name="keywords"]', "name", "keywords", kws.join(", "));
      }
    })();

    return () => { cancelled = true; };
  }, [pathname]);

  return null;
};

export default EntitySeoAutoLoader;
